import { type ReactElement, type ReactNode } from 'react';
import { Calendar, Clock } from 'lucide-react';
import { Field } from '@/components/field';
import { dateFieldOf } from '@/lib/format';
import { useTranslation } from '@/lib/i18n';

// A tap has focused the field whatever happens here; the picker is the bonus.
export function openPicker(input: HTMLInputElement): void {
  try {
    input.showPicker();
  } catch {
    return;
  }
}

// iOS draws its date and time controls as wide as the value they show and will
// not shrink them, so two never fit a phone row and the second runs off the
// screen (§20). The control below is the native one — tapping it opens the
// native picker — but out of flow, clipped and invisible, so what the row is
// sized by is our own text.
function PickerField({
  label,
  type,
  value,
  min,
  icon,
  shows,
  onPick,
}: {
  label: string;
  type: 'date' | 'time';
  value: string;
  min?: string;
  icon: ReactNode;
  shows: string;
  onPick: (value: string) => void;
}): ReactElement {
  return (
    <Field label={label}>
      <span className="relative flex h-11 min-w-0 items-center gap-2 overflow-hidden rounded-xl border bg-background px-3 text-base focus-within:ring-2 focus-within:ring-ring">
        {icon}
        <span className="min-w-0 truncate">{shows}</span>
        <input
          type={type}
          aria-label={label}
          value={value}
          min={min}
          onChange={(event) => onPick(event.target.value)}
          onClick={(event) => openPicker(event.currentTarget)}
          className="absolute inset-0 h-full w-full appearance-none text-base opacity-0"
        />
      </span>
    </Field>
  );
}

export function DateField(props: {
  label: string;
  value: string;
  min?: string;
  onPick: (date: string) => void;
}): ReactElement {
  const { language } = useTranslation();
  return (
    <PickerField
      {...props}
      type="date"
      icon={<Calendar className="size-4 shrink-0 text-muted-foreground" aria-hidden />}
      shows={dateFieldOf(props.value, language)}
    />
  );
}

// An hour nobody has given yet reads as the blank a native control shows.
export function TimeField(props: {
  label: string;
  value: string;
  onPick: (time: string) => void;
}): ReactElement {
  return (
    <PickerField
      {...props}
      type="time"
      icon={<Clock className="size-4 shrink-0 text-muted-foreground" aria-hidden />}
      shows={props.value === '' ? '--:--' : props.value}
    />
  );
}
